import * as _ from "lodash";
import { v3 } from "oc-threejs/short3";
import { Box3, Vector3 } from "three";

/**
 * Für Doku siehe den SourceCode von
 * http://preview.objectcode.de/oc-base Examples/#oc-threejs.PositioningAPI
 *
 * Die PositioningApi bezieht sich immer auf die BoundingBox. Normalerweise ist diese in World-Space, entsprechend
 * werden die Vektoren der PositioningApi auch in WorldSpace zurückgegeben.
 */
export class PosBox {
  object: any;
  box: Box3;
  _span: any;
  positionRelativeToGridCenterInGridProportion?: Vector3;

  // Dynamic properties
  declare min: IPosBoxVector;
  declare max: IPosBoxVector;
  declare center: IPosBoxVector;
  declare bottomCenter: IPosBoxVector;
  declare topCenter: IPosBoxVector;
  declare left: number;
  declare right: number;
  declare bottom: number;
  declare top: number;
  declare back: number;
  declare front: number;
  declare size: IPosBoxVector;

  [key: string]: any;

  constructor(object: any, boundingBox?: Box3) {
    if (object instanceof Box3) {
      this.box = object;
    } else {
      this.object = object;
      this.box = boundingBox
        ? boundingBox
        : object.getBoundingBox
          ? object.getBoundingBox()
          : new Box3().setFromObject(this.object);
    }

    if (this.object) {
      const worldPosition = this.object.getWorldPosition(v3());
      this.box.min.sub(worldPosition);
      this.box.max.sub(worldPosition);
      const size = this.box.getSize(v3());
      if (size.x != 0 && size.y != 0 && size.z != 0) {
        this.positionRelativeToGridCenterInGridProportion = v3().sub(this.box.min).divide(size);
      }
    }

    this._span = new PosBoxSpan(this);
  }

  getWorldPosition(input: Vector3 = v3()): Vector3 {
    if (this.object) {
      return this.object.getWorldPosition(input);
    }
    return input;
  }

  get position(): IPosBoxVector {
    return new PosBoxVector(
      this,
      this.positionRelativeToGridCenterInGridProportion || v3(),
      translationGetSet,
    );
  }

  set position(newPosition: Vector3) {
    const delta = newPosition.clone().sub(this.object.getWorldPosition(v3()));
    if (this.object) {
      this.object.translate(delta);
    } else {
      this.box.translate(delta);
    }
  }

  get span(): any {
    return this._span;
  }

  grid(proportion: any, y?: number, z?: number): IPosBoxVector {
    if (y != null && z != null) {
      proportion = v3(proportion, y, z);
    } else if (_.isNumber(proportion)) {
      proportion = v3(proportion, proportion, proportion);
    }
    return new PosBoxVector(this, proportion, translationGetSet);
  }

  translate(axis: string, delta: number): void {
    if (this.object) {
      this.object.position[axis] += delta;
    } else {
      this.box.min[axis] += delta;
      this.box.max[axis] += delta;
    }
  }

  resize(axis: string, delta: number, proportion: number): void {
    if (Math.abs(delta) < 0.00000000001) {
      return;
    }
    const oldSize = this.box.max[axis] - this.box.min[axis];
    const scaleDelta = oldSize == 0 ? 0 : Math.abs((oldSize + delta) / oldSize);
    const translation = (proportion - 0.5) * delta;
    if (this.object) {
      const oldScale = (this.object.scale as any)[axis];
      const newScale = scaleDelta * oldScale;
      this.setScale(axis, newScale);
    }
    this.resizeBox(axis, delta);
    this.translate(axis, translation);
  }

  resizeBox(axis: string, delta: number): void {
    this.box.min[axis] -= delta / 2;
    this.box.max[axis] += delta / 2;
  }

  setScale(axis: string, newScale: number): void {
    if (this.object) {
      this.object.scale[axis] = newScale;
    }
  }

  toString(): string {
    return this.grid(0) + "." + this.grid(1);
  }
}

function proportionGet(instance: any, posBox: PosBox, axis: string, proportion: number): number {
  return (
    posBox.box.min[axis] * (1 - proportion) +
    posBox.box.max[axis] * proportion +
    posBox.getWorldPosition(v3())[axis]
  );
}

function proportionDelta(
  instance: any,
  posBox: PosBox,
  axis: string,
  name: string,
  value: number,
): number {
  return value - instance[name];
}

const translationGetSet = {
  get: proportionGet,
  set: function (
    instance: any,
    posBox: PosBox,
    axis: string,
    name: string,
    proportion: number,
    value: number,
  ) {
    const delta = proportionDelta(instance, posBox, axis, name, value);
    posBox.translate(axis, delta);
  },
};

const resizeGetSet = {
  get: proportionGet,
  set: function (
    instance: any,
    posBox: PosBox,
    axis: string,
    name: string,
    proportion: number,
    value: number,
  ) {
    const delta = value - instance[name];
    posBox.resize(axis, delta, proportion);
  },
};

function axisProps(getset: any, posBoxGet: (inst: any) => PosBox) {
  return {
    min: vectorProperty("min", v3(0, 0, 0), getset, posBoxGet),
    max: vectorProperty("max", v3(1, 1, 1), getset, posBoxGet),
    center: vectorProperty("center", v3(0.5, 0.5, 0.5), getset, posBoxGet),
    bottomCenter: vectorProperty("bottomCenter", v3(0.5, 0, 0.5), getset, posBoxGet),
    topCenter: vectorProperty("topCenter", v3(0.5, 1, 0.5), getset, posBoxGet),
    left: axisProperty("left", "x", v3(0, 0, 0), getset, posBoxGet),
    right: axisProperty("right", "x", v3(1, 1, 1), getset, posBoxGet),
    bottom: axisProperty("bottom", "y", v3(0, 0, 0), getset, posBoxGet),
    top: axisProperty("top", "y", v3(1, 1, 1), getset, posBoxGet),
    back: axisProperty("back", "z", v3(0, 0, 0), getset, posBoxGet),
    front: axisProperty("front", "z", v3(1, 1, 1), getset, posBoxGet),
  };
}

function posBoxVectorAccessor(axis: string) {
  return {
    get: function () {
      // @ts-ignore
      return this.getset.get(this, this.posBox, axis, this.proportion[axis]);
    },
    set: function (value: number) {
      // @ts-ignore
      this.getset.set(this, this.posBox, axis, axis, this.proportion[axis], value);
    },
  };
}

function vectorProperty(
  name: string,
  proportion: Vector3,
  getset: any,
  posBoxGet: (inst: any) => PosBox,
) {
  const internName = "_" + name;
  return {
    get: function () {
      if (this[internName] === undefined) {
        this[internName] = new PosBoxVector(posBoxGet(this), proportion, getset);
      }
      return this[internName];
    },
    set: function (v: Vector3) {
      const shadowVector = this[name];
      shadowVector.x = v.x;
      shadowVector.y = v.y;
      shadowVector.z = v.z;
    },
  };
}

function axisProperty(
  name: string,
  axis: string,
  proportion: Vector3 | any,
  getset: any,
  posBoxGet: (inst: any) => PosBox,
) {
  return {
    get: function () {
      return getset.get(this, posBoxGet(this), axis, proportion[axis]);
    },
    set: function (value: number) {
      getset.set(this, posBoxGet(this), axis, name, proportion[axis], value);
    },
  };
}

export interface IPosBoxVector extends Vector3 {
  posBox: PosBox;
  proportion: Vector3;
  getset: Function;
}

function PosBoxVector(posBox: PosBox, proportion: Vector3, getset: any) {
  // @ts-ignore
  this.posBox = posBox;
  // @ts-ignore
  this.proportion = proportion;
  // @ts-ignore
  this.getset = getset;
}

PosBoxVector.prototype = Object.create(Vector3.prototype);

Object.defineProperties(PosBoxVector.prototype, {
  x: posBoxVectorAccessor("x"),
  y: posBoxVectorAccessor("y"),
  z: posBoxVectorAccessor("z"),
});

Object.defineProperties(
  PosBox.prototype,
  axisProps(translationGetSet, (posBox) => posBox),
);

Object.defineProperties(PosBox.prototype, {
  size: vectorProperty(
    "size",
    v3(0.5, 0.5, 0.5),
    {
      get: function proportionGet(instance: any, posBox: PosBox, axis: string) {
        return posBox.box.getSize(v3())[axis as keyof Vector3];
      },
      set: function (
        instance: any,
        posBox: PosBox,
        axis: string,
        name: string,
        proportion: number,
        value: number,
      ) {
        const delta = value - instance[name];
        posBox.resize(axis, delta, proportion);
      },
    },
    (posBox) => posBox,
  ),
});

function PosBoxSpan(posBox: PosBox) {
  // @ts-ignore
  this.posBox = posBox;
}

Object.defineProperties(
  PosBoxSpan.prototype,
  axisProps(resizeGetSet, (posBoxSpan) => posBoxSpan.posBox),
);
